import React, { useEffect, useRef } from 'react';
import { View, Text } from 'react-native';
import i18n from 'i18n-js';

import Svg from '@styles/icons/svg';
import { font as initialFont } from '@styles/fonts';
import { SmallLoading } from '@components/loading';
import TouchableElement from '@components/common/touchableElement';
import { scale as initialScale } from '@components/frame';
import { isPhone } from '@helpers';
import { useDeviceSize } from '@v2/pages/common/hooks/useDeviceSize';
import { SCREEN_BORDER_RADIUS } from '@v2/pages/common/constants/base.constants';

import { FilterTileProps } from './types';

const FilterTile: React.FC<FilterTileProps> = ({
  scale = initialScale,
  font = initialFont,
  name,
  value,
  elementWidth,
  loaded,
  icon,
  isSelected,
  hasActiveSelection,
  onPress,
  C,
}) => {
  const { IS_NARROW_WEB } = useDeviceSize();

  const renderAmount = value || 0;

  const isFrozen = hasActiveSelection && !isSelected;

  const lastUnfilteredValueRef = useRef<number | null>(null);
  useEffect(() => {
    if (!hasActiveSelection) {
      lastUnfilteredValueRef.current = renderAmount;
    }
  }, [hasActiveSelection, renderAmount]);

  const hasSnapshot = lastUnfilteredValueRef.current !== null;
  const displayedAmount = isFrozen && hasSnapshot ? lastUnfilteredValueRef.current : renderAmount;
  const displayedLoaded = isFrozen && hasSnapshot ? true : loaded;

  return (
    <TouchableElement onPress={onPress}>
      {({ boxStyle }) => (
        <View
          style={[
            boxStyle,
            {
              width: elementWidth,
              height: scale(isPhone ? 44 : 56),
              minHeight: scale(isPhone ? 44 : 56),
              borderRadius: scale(SCREEN_BORDER_RADIUS),
              borderWidth: isSelected ? 2 : 1,
              borderColor: isSelected ? C('Link/Light/Primary ') : C('Hairline/Light/No trans'),
              paddingHorizontal: 7,
              backgroundColor: C('Background/Light/Field'),
            },
          ]}
        >
          <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: scale(isPhone ? 2 : 4), gap: scale(6) }}>
            <Svg icon={icon.name} width={scale(16)} height={scale(16)} color={icon.color} />
            <View
              style={[
                {
                  flexDirection: 'row',
                  justifyContent: 'flex-start',
                  height: scale(isPhone ? 20 : 24),
                  alignItems: 'center',
                },
                isPhone && { transform: [{ translateY: !IS_NARROW_WEB ? -2 : -1 }] },
              ]}
            >
              {displayedLoaded ? (
                <Text style={[font[isPhone ? 'Headline' : 'Headline Large'], { color: C('Label/Light/Primary') }]}>{displayedAmount}</Text>
              ) : (
                <View
                  style={{
                    transform: [{ scale: 0.8 }, { translateY: isPhone && !IS_NARROW_WEB ? 2 : IS_NARROW_WEB ? 1 : 0 }],
                    height: scale(isPhone ? 20 : 24),
                    justifyContent: 'center',
                  }}
                >
                  <SmallLoading />
                </View>
              )}
            </View>
          </View>
          <Text
            numberOfLines={1}
            ellipsizeMode="tail"
            style={[
              isPhone ? font['Caption 1'] : font.Body,
              {
                color: isPhone ? C('Label/Light/Secondary') : C('Label/Light/Tertiary'),
              },
            ]}
          >
            {i18n.t(name)}
          </Text>
        </View>
      )}
    </TouchableElement>
  );
};

export default FilterTile;
